import type { InstallmentsSummary } from '@/services';
import { formatCurrency } from '@/shared/utils/currencyFormatter';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface SummaryCardsProps {
  summary?: InstallmentsSummary;
  loading?: boolean;
  /** Situação da parcela atualmente filtrada (dv_situacao) para destacar o card. */
  activeStatus: string;
  onSelectStatus: (status: string) => void;
}

interface CardConfig {
  key: keyof InstallmentsSummary;
  label: string;
  icon: LucideIcon;
  iconClass: string;
  /** dv_situacao aplicado ao clicar; '' limpa o filtro (Geradas = todas). */
  filterValue: string;
}

const CARD_BASE_CLASS =
  'flex flex-col gap-1 rounded-2xl bg-white px-3 py-3 text-left shadow-[0px_4px_24px_rgba(15,22,30,0.08)] transition-shadow hover:shadow-[0px_6px_28px_rgba(15,22,30,0.12)] lg:px-4';
const ICON_WRAP_CLASS =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-alba-gray-100 lg:h-9 lg:w-9';
const LABEL_CLASS = 'text-xs leading-tight text-alba-gray-600 lg:text-sm';
const VALUE_CLASS =
  'w-full truncate text-right font-gotham text-lg font-bold leading-none text-alba-black-500 tabular-nums lg:text-2xl';

const CARDS: CardConfig[] = [
  {
    key: 'generated',
    label: 'Geradas',
    icon: FileText,
    iconClass: 'text-alba-blue-500',
    filterValue: '',
  },
  {
    key: 'paid',
    label: 'Pagas',
    icon: CheckCircle2,
    iconClass: 'text-alba-green-500',
    filterValue: '4',
  },
  {
    key: 'overdue',
    label: 'Inadimplentes',
    icon: AlertTriangle,
    iconClass: 'text-alba-error-red-500',
    filterValue: '2',
  },
  {
    key: 'toExpire',
    label: 'A vencer',
    icon: Clock,
    iconClass: 'text-amber-500',
    filterValue: '3',
  },
  {
    key: 'cancelled',
    label: 'Canceladas',
    icon: XCircle,
    iconClass: 'text-alba-gray-500',
    filterValue: '1',
  },
];

export function InstallmentsSummaryCards({
  summary,
  loading,
  activeStatus,
  onSelectStatus,
}: SummaryCardsProps) {
  return (
    <div className='grid w-full grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:gap-4'>
      {CARDS.map(card => {
        const Icon = card.icon;
        const bucket = summary?.[card.key];
        // Geradas fica ativo quando não há filtro; os demais quando batem o dv_situacao.
        const active = card.filterValue
          ? activeStatus === card.filterValue
          : activeStatus === '';
        const display = formatCurrency(bucket?.amount ?? 0);
        return (
          <button
            key={card.key}
            type='button'
            onClick={() => onSelectStatus(card.filterValue)}
            className={`${CARD_BASE_CLASS} ${
              active ? 'ring-2 ring-alba-blue-500' : ''
            }`}
          >
            <div className='flex items-center justify-between gap-2'>
              <div className={ICON_WRAP_CLASS}>
                <Icon
                  className={`h-4 w-4 lg:h-5 lg:w-5 ${card.iconClass}`}
                  strokeWidth={1.6}
                />
              </div>
              {loading ? (
                <div className='h-7 w-24 animate-pulse rounded-md bg-alba-gray-200' />
              ) : (
                <div className='flex min-w-0 flex-1 items-center justify-end'>
                  <span title={display} className={VALUE_CLASS}>
                    {display}
                  </span>
                </div>
              )}
            </div>
            <span className={LABEL_CLASS}>{card.label}</span>
            {!loading && (
              <span className='text-xs text-alba-gray-600'>
                {bucket?.count ?? 0} parcelas
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
